import React, { useState } from 'react';
import './Modal.css';
import './ConversationFeedbackModal.css';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';
import { isFeedbackRequired } from '../../utilities/feedbackValidation';
import ConversationProgressSummary from '../ConversationProgressSummary';

// Tags for low ratings (1-3 stars)
const NEGATIVE_FEEDBACK_TAGS = [
  'feedback_tag_bug',
  'feedback_tag_not_understood',
  'feedback_tag_not_interesting',
  'feedback_tag_too_hard',
  'feedback_tag_too_easy',
  'feedback_tag_audio_issues',
  'feedback_tag_too_slow',
  'feedback_tag_repetitive',
];

// Tags for high ratings (4-5 stars)
const POSITIVE_FEEDBACK_TAGS = [
  'feedback_tag_good_topic',
  'feedback_tag_right_level',
  'feedback_tag_good_feedback',
  'feedback_tag_understood_me',
  'feedback_tag_learned_something',
  'feedback_tag_fun',
  'feedback_tag_natural_conversation',
];

const ConversationFeedbackModal = ({ isOpen, onClose, onSubmit, onSkip, conversationProgress = null }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [comment, setComment] = useState('');

  // Every rating needs context, high or low: at least one tag or a comment, so
  // the score always comes with a reason we can act on.
  const requireFeedback = isFeedbackRequired(rating, selectedTags, comment);

  const handleSubmit = () => {
    if (requireFeedback) return; // Guard the Enter-key/form-submit path too
    onSubmit({ rating, tags: selectedTags, comment });
    // Reset state after submit
    setRating(0);
    setSelectedTags([]);
    setComment('');
  };

  const handleSkip = () => {
    onSkip();
    // Reset state after skip
    setRating(0);
    setSelectedTags([]);
    setComment('');
  };

  const handleClose = () => {
    onClose();
    // Reset state after close
    setRating(0);
    setSelectedTags([]);
    setComment('');
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Show tags when a rating has been selected
  const showTags = rating > 0;
  // Get the appropriate tags based on rating
  const currentTags = rating <= 3 ? NEGATIVE_FEEDBACK_TAGS : POSITIVE_FEEDBACK_TAGS;
  // Hide Skip once a rating is picked so the rating gets submitted with its
  // context rather than skipped past it. Closing the modal still works.
  const hideSkip = rating > 0;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal feedback-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
      >
        <form className="modal-content" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="modal-header">
            <span id="feedback-modal-title">{t('conversation_feedback_title')}</span>
            <button
              type="button"
              onClick={handleClose}
              className="modal-close-button"
              aria-label={t('close') || 'Close'}
            >
              <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
            </button>
          </div>

          <div className="modal-body">
            {conversationProgress && (
              <ConversationProgressSummary conversationProgress={conversationProgress} />
            )}
            {/* Star Rating */}
            <div className="star-rating-container">
              <div className="star-rating" role="radiogroup" aria-label={t('conversation_feedback_title')}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`star-button ${star <= (hoveredRating || rating) ? 'active' : ''}`}
                    onClick={() => {
                      // Clear selected tags when crossing the 3-star threshold (different tag sets)
                      if ((rating <= 3 && star > 3) || (rating > 3 && star <= 3)) {
                        setSelectedTags([]);
                      }
                      setRating(star);
                    }}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    aria-label={`${star} ${star === 1 ? t('star') : t('stars')}`}
                    aria-pressed={star === rating}
                  >
                    <FontAwesomeIcon
                      icon={star <= (hoveredRating || rating) ? faStar : faStarOutline}
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Feedback Tags - shown for all ratings, a tag or comment is required */}
            {showTags && (
              <div className="feedback-tags-container">
                <p className="feedback-tags-label">
                  {rating <= 3 ? t('feedback_tags_label') : t('feedback_tags_label_positive')}
                </p>
                <div className="feedback-tags">
                  {currentTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className={`feedback-tag ${selectedTags.includes(tag) ? 'selected' : ''}`}
                      onClick={() => toggleTag(tag)}
                      aria-pressed={selectedTags.includes(tag)}
                    >
                      {t(tag)}
                    </button>
                  ))}
                </div>
                <div className="feedback-comment-container">
                  <textarea
                    className="feedback-comment"
                    placeholder={t('feedback_comment_placeholder')}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                  />
                </div>
                {requireFeedback && (
                  <p className="feedback-required-hint" role="alert">
                    {t('feedback_required_hint', 'Please pick a tag or add a comment so we can improve.')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer with Skip and Submit buttons. Skip is hidden once a rating is picked. */}
          <div className={`modal-footer feedback-footer${hideSkip ? ' feedback-footer--single' : ''}`}>
            {!hideSkip && (
              <button
                type="button"
                className="modal-button skip-button"
                onClick={handleSkip}
              >
                {t('skip')}
              </button>
            )}
            <button
              type="submit"
              className="modal-button submit-button"
              disabled={requireFeedback}
            >
              {t('submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConversationFeedbackModal;
